import type { FC } from 'react';
import React, { useContext } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

import { HomeIconComponent } from '../GlobalNavigationIcons.tsx';

import type { GlobalItemProps } from './globalItemProps.ts';

const i18n = defineMessages({
	CustomHomeLink: {
		id: 'side-navigation.custom.home.link',
		description:
			'link to the a special custom Home page that was set by a site admin. In addition to "your-work" it adds an additional item to the nav that links to a specific space overview',
		defaultMessage: 'Custom Home Page',
	},
});

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks.ts'))
			.fireCustomHomepageClickedAnalytics,
);

export const CustomHomeItem: FC<GlobalItemProps> = () => {
	//takes globalItemProps but is not customizable so doesn't use them
	const { location, match } = useContext(RoutesContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, match?.name);
	const { homepageUri, homepageTitle } = useContext(SPAViewContext);

	const isSelected =
		!!location &&
		location.pathname === `${CONTEXT_PATH}${homepageUri}` &&
		location.search.includes('mode=global');

	return (
		<LinkMenuItem
			href={`${CONTEXT_PATH}${homepageUri}?mode=global`}
			elemBefore={HomeIconComponent}
			isSelected={isSelected}
			onClick={fireClickAnalytics}
		>
			{homepageTitle || <FormattedMessage {...i18n.CustomHomeLink} />}
		</LinkMenuItem>
	);
};
